import axios, { ResponseType } from 'axios';
import moment from 'moment';
import { APIResponse, EventDate, Expense, ExpenseType, ExpenseRejectReason, ExpensesReportSummary, EventsReportSummary, PaymentMethod, BankAccountForm, BankAccount, UserBankAccount, EventStatusType, BudgetForm, Budget, EventDateList, EventDateDays, WorkerForm, WorkerTitle, Worker, EventPayout, EventDateMetadata, Metadata, Location, Promoter, AssetType, UserAssetType, Asset, EventComment, PromoterContact, WorkerBanReason, UserTag, InventoryType, InventoryWarehouse, InventoryItem, GroupedItemsByType, InventoryItemAssetType, ShippingCarrier, RFCWorker, AvailableItemForShipment, Shipment, Contract, ConfirmationData, Request, IssueType, Issue, IssueComment } from './SiteClasses';

export const CDN_URL = process.env.REACT_APP_CDN_URL;
export const PAGE_URL = process.env.REACT_APP_PAGE_URL;
export const GOOGLE_KEY = process.env.REACT_APP_PLACES_KEY;
export const PAGE = process.env.REACT_APP_PAGE;

const PAGE_AUTH = process.env.REACT_APP_AUTH_URL;
const ROOT_URL = process.env.REACT_APP_API_URL;
const DEBUG = process.env.REACT_APP_DEBUG==='1';
const LOGIN_SOURCE = 3;

export interface TokenData{
	access: string,
	expires: number,
}
export interface UserLogin{
	user: {
		user_id: number,
		email: string,
		first_name: string,
		last_name: string,
		name: string,
		superadmin: boolean,
		change_password: boolean,
		notifications: number,
		tags: {
			tag_id: number,
			tag_name: string
		}[]
	},
	access: number[]
}

interface PostOptions{
	login?: boolean,
	response_type?: ResponseType,
	access_token?: string,
}

var CACHE : { [x: string]: APIResponse<any> } = {};
var USER_LOGIN_CACHE : UserLogin = null;

var getToken = () : TokenData=>{
	var jsonstr = localStorage.getItem('access_token');
	if(!jsonstr) return null;
	try{
		var data = JSON.parse(jsonstr) as TokenData;
		if(!data) return null;
		if(!data.access) return null;
		var now = moment().unix();
		if(data.expires<=(now+30)) return null;
		return data;
	}catch(e){
		return null;
	}
}

var saveToken = (token_data: TokenData)=>{
	localStorage.setItem('access_token', JSON.stringify(token_data));
}

var logout = ()=>{
	localStorage.removeItem('access_token');
	localStorage.removeItem('user_data');
	window.dispatchEvent(new Event('user_data'));
	window.dispatchEvent(new Event('user_logout'));
}

async function post<T=any>(endpoint: string, data?: any, opts: PostOptions={}) : Promise<APIResponse<T>>{
	var options = {
		headers: {
			'show-code': DEBUG ? 1 : 0,
			'token': '',
			'Content-Type': 'application/json'
		}
	}
	if(opts?.login!==false){
		if(opts?.access_token){
			options.headers.token = opts.access_token;
		}else{
			var u = getToken();
			if(u?.access){
				options.headers.token = u.access;
			}
		}
	}
	if(data instanceof FormData){
		options.headers['Content-Type'] = 'multipart/form-data';
	}

	try{
		var ENDPOINT = `${ROOT_URL}/${endpoint}`;
		if(DEBUG) console.log("POST "+ENDPOINT);
		var res = await axios.post(ENDPOINT, data, {
			...options,
			responseType: opts?.response_type || 'json'
		});
		if(DEBUG) console.log(` => Response: ${(res.headers['content-length']/1024).toFixed(2)} KB`);
		if(res.data && res.data.error && (res.data.code==='P-TKNE' || res.data.code==='P-TKNV')){
			logout();
		}
		return res.data as APIResponse<T>;
	}catch(err){
		if(DEBUG) console.error(err);
		if((err as any)?.code==="ERR_NETWORK"){
			return {
				error: true,
				message: 'No se pudo establecer una conexión con el servidor. (API-NONTWK)',
				code: 'API-NONTWK',
			}
		}
		return {
			error: true,
			message: 'Hubo un error inesperado (API-LCL-1)',
			code: 'API-LCL-1',
		}
	}
};

var cachePost = async <T=any>(endpoint: string, key: string, data?: any) : Promise<APIResponse<T>>=>{
	if(CACHE[key]) return CACHE[key] as APIResponse<T>;
	var res = await post(endpoint, data);
	if(!res.error) CACHE[key] = res;
	return res;
}

var getAuthUrl = ()=>{
	if(!PAGE_AUTH) return null;
	return `${PAGE_AUTH}/admin?s=${LOGIN_SOURCE}&continue=${encodeURIComponent(`${window.location.origin}/auth`)}&redirect=${encodeURIComponent(window.location.href)}`
}




var onLoginToken = async(token: string, expiration: number, dispatch_event: boolean=true)=>{
	var res = await post<{ token: { access: string, expires: number } }>('auth/login', { token });
	if(!res.error){
		saveToken(res.data.token);
		if(dispatch_event) window.dispatchEvent(new Event('user_token'));
		getLogin(true, res.data.token.access, true);
	}
	return res;
}

var getLogin = async(force: boolean=false, token: string=null, dispatch_event: boolean=true)=>{
	if(!force && USER_LOGIN_CACHE) return { error: false, data: USER_LOGIN_CACHE, message: '' }

	var access_token = token;
	if(!access_token){
		var tkn = getToken();
		if(!tkn) return { error: true, message: 'El usuario no tiene un token de autorización', code: 'NOTKN' };
		access_token = tkn.access;
	}

	var res = await post<UserLogin>('users/me', {}, {
		access_token: token
	});
	if(res.error) return res;
	USER_LOGIN_CACHE = res.data;
	localStorage.setItem('user_data', JSON.stringify(res.data));
	setTimeout(()=>{
		if(dispatch_event) window.dispatchEvent(new Event('user_data'));
	}, 50);
	return res;
}

var getUsers = async(count?: number, offset?: number)=>{
	return post('users/list', { offset, count });
}

var getUser = async(user_id: number, events: boolean=false, assets: boolean=false)=>{
	return post('users/get', { user_id, events, assets });
}

var getNotifications = async(offset: number = 0, count: number = 20)=>{
	return post('users/notifications', { offset, count });
}

var getNotificationsInbox = async(offset: number = 0, count: number = 20, query?: string, only_unread?: boolean, type_filter?: number)=>{
	return post('users/notifications/inbox', { offset, count, query, only_unread, type_filter });
}

var dismissNotification = async(notification_id: number)=>{
	return post('users/notifications/dismiss', { notification_id });
}

var dismissAllNotifications = async()=>{
	return post('users/notifications/dismiss/all');
}

var searchUsers = async(query: string, offset?: number, count?: number, tag_id?: number)=>{
	return post('users/search', { query, offset, count, tag_id });
}

var userAccess = async(user_id: number, access: number[])=>{
	return post('users/access', { user_id, access });
}

var getAllTags = async()=>{
	return post<UserTag[]>('users/tags/list');
}

var editUserTags = async(user_id: number, tags: number[])=>{
	return post('users/tags/edit', { user_id, tags });
}

var editUser = async(user_id: number, first_name: string, last_name: string, payout_type: number)=>{
	return post('users/edit', { user_id, first_name, last_name, payout_type });
}

var setUserPayoutType = async(user_id: number, payout_type: number)=>{
	return post('users/payout-type', { user_id, payout_type });
}

var getPayoutTypes = async()=>{
	return post<{ type_id: number, type_name: string }[]>('users/payout-types', {});
}

var getUserExpenses = async(query?: string, offset?: number, count?: number)=>{
	return post<Expense[]>('users/expenses', { query, offset, count });
}

var getUserBudgets = async(query?: string, offset?: number, count?: number)=>{
	return post<Budget[]>('users/budgets', { query, offset, count });
}

var getUserRequests = async(query?: string, offset?: number, count?: number, status_filters: number[] = [])=>{
	return post<Request[]>('users/requests', { query, offset, count, status_filters });
}

var getEvents = async(count: number, offset: number, date_start: Date = null, date_end: Date = null, user_id: number = null, executive_id: number = null)=>{
	return post<EventDateList[]>('events/list', { offset, count, date_start, date_end, user_id, executive_id });
}

var getMyEvents = async(offset: number, count: number)=>{
	return post<EventDateList[]>('events/my', { offset, count });
}

var getEventsCount = async(user_id: number = null)=>{
	return post<{ operating: number, closed: number }>('events/count', { user_id });
}

var getEvent = async(date_id: number, users: boolean=false)=>{
	return post('events/get', { date_id, users });
}

var searchEvents = async(query: string, offset?: number, count?: number)=>{
	return post<EventDateList[]>('events/search', { query, offset, count });
}

var getMonthEvents = (months: { month: number, year: number }[], user_id: number = null, executive_id: number = null)=>{
	return post<EventDateDays[]>('events/months', { months, user_id, executive_id });
}

var getMonthStatusStats = (month: number, year: number, user_id: number = null, executive_id: number = null)=>{
	return post<{ total: number, operating: number, promoter: number, not_operated: number, postponed: number, cancelled: number, closed: number, unconfigured: number }>('events/month/stats', { month, year, user_id, executive_id });
}

var assingEventUser = async(date_id: number, user_id: number, type_id: number)=>{
	return post('events/assign', {date_id, user_id, type_id });
}

var assignEventCoordinator = async(date_id: number, user_id: number)=>{
	return post('events/assign/coordinator', { date_id, user_id });
}

var assignEventExecutive = async(date_id: number, user_id: number)=>{
	return post('events/assign/executive', { date_id, user_id });
}

var assignEventPromoterContact = async(date_id: number, contact_id: number)=>{
	return post('events/promoter/contact', { date_id, contact_id });
}

var assignParentEvent = async(date_id: number, parent_date_id: number)=>{
	return post('events/parent/assign', { date_id, parent_date_id });
}

var updateEventDate = async(date_id: number, date: number)=>{
	return post('events/date', { date_id, date });
}

var unassignEvent = async(date_id: number)=>{
	return post('events/parent/unassign', { date_id });
}

var updateEventStatus = (date_id: number, status_id: EventStatusType, location: Location=null, parent_date_id: number = null)=>{
	return post('events/status', { date_id, status_id, location, parent_date_id });
}

var addEventLocation = (date_id: number, location: Location)=>{
	return post<{ location_id: number }>('events/location', { date_id, location });
}

var closeEvent = (date_id: number)=>{
	return post<{ errors?: string[] }>('events/close', { date_id });
}

var getEventExpenses = async (date_id: number, count?: number, offset?: number)=>{
	return post<Expense[]>('events/expenses', { date_id, count, offset });
}

var getEventBudgets = async (date_id: number, count?: number, offset?: number)=>{
	return post<Budget[]>('events/budgets', { date_id, count, offset });
}

var getEventWorkers = async (date_id: number, count?: number, offset?: number)=>{
	return post('events/workers', { date_id, count, offset });
}

var addEventWorkerRequest = async (date_id: number, title_id: number, quantity: number)=>{
	return post<{ request_id: number }>('events/workers/request/add', { date_id, title_id, quantity });
}

var deleteEventWorkerRequest = async (request_id: number)=>{
	return post('events/workers/request/delete', { request_id });
}

var getEventPayouts = async (date_id: number, count?: number, offset?: number)=>{
	return post<EventPayout[]>('events/payouts', { date_id, count, offset });
}

var getEventAssets = async (date_id: number)=>{
	return post<Asset[]>('events/assets', { date_id });
}

var createEventComment = async(date_id: number, content: string, mentioned_users: number[] = null)=>{
	return post('events/comments/create', { date_id, content, mentioned_users });
}

var deleteEventComment = async(comment_id: number, deleted: boolean=true)=>{
	return post('events/comments/delete', { comment_id, deleted });
}

var editEventComment = async(comment_id: number, content: string)=>{
	return post('events/comments/edit', { comment_id, content });
}

var getEventComments = async(date_id: number, count?: number, offset?: number)=>{
	return post<EventComment[]>('events/comments/get', { date_id, count, offset });
}





var getExpense = async (expense_id: number)=>{
	return post<Expense>('expenses/get', { expense_id });
}

var getExpenses = async (count: number, offset: number, pendings: boolean=false, department_ids: number[] = [], type_id: number = null, status_filters: number[] = [], date_start: number = null, date_end: number = null)=>{
	return post('expenses/list', { offset, count, pendings, department_ids, type_id, status_filters, date_start, date_end });
}

var searchExpenses = async (query: string, status_filters?: number[], offset?: number, count?: number)=>{
	return post<Expense[]>('expenses/search', { query, status_filters, offset, count });
}

var getExpenseTypes = async ()=>{
	return post<ExpenseType[]>('expenses/type/list');
}

var createExpenseType = async (type_name: string)=>{
	return post<{ type_id: number }>('expenses/type/create', { type_name });
}

var editExpenseType = async (type_id: number, type_name: string)=>{
	return post('expenses/type/edit', { type_id, type_name });
}

var deleteExpenseType = async (type_id: number)=>{
	return post('expenses/type/delete', { type_id });
}

var getPaymentMethods = async ()=>{
	return post<PaymentMethod[]>('expenses/method/list');
}

var createPaymentMethod = async (method_name: string)=>{
	return post<{ method_id: number }>('expenses/method/create', { method_name });
}

var editPaymentMethod = async (method_id: number, method_name: string)=>{
	return post('expenses/method/edit', { method_id, method_name });
}

var deletePaymentMethod = async (method_id: number)=>{
	return post('expenses/method/delete', { method_id });
}

var createExpense = async (expense_title: string, date_id: number, type_id: number, method_id: number, department_id: number, amount: number, description: string, account_id: number, file?: File, fileTypeId?: number)=>{
	var d = new FormData();
	d.append('expense_title', expense_title);
	d.append('date_id', date_id?.toString());
	d.append('type_id', type_id.toString());
	d.append('method_id', method_id.toString());
	d.append('department_id', department_id.toString());
	d.append('amount', amount.toString());
	d.append('description', description || '');
	d.append('account_id', account_id?.toString());

	if(file && fileTypeId){
		d.append('asset_type_id', fileTypeId.toString());
		d.append('asset_name', expense_title);
		d.append('asset_description', description);
		d.append('file', file);
	}

	return post<{ expense_id: number, approved: boolean }>('expenses/create', d);
}

var editExpense = async (expense_id: number, expense_title: string, date_id: number, type_id: number, method_id: number, department_id: number, amount: number, description: string, account_id: number)=>{
	return post('expenses/edit', { expense_id, expense_title, date_id, type_id, method_id, department_id, amount, description, account_id });
}

var paidExpense = async (expense_id: number, paid: boolean = true)=>{
	return post('expenses/paid', { expense_id, paid });
}

var deleteExpense = async (expense_id: number, deleted: boolean = true)=>{
	return post('expenses/delete', { expense_id, deleted });
}

var rejectExpense = async (expense_id: number, reject_reason: number)=>{
	return post('expenses/reject', { expense_id, reject_reason });
}

var getExpenseRejectReasons = async ()=>{
	return post<ExpenseRejectReason[]>('expenses/reject/reasons');
}

var createExpenseRejectReason = async (reason: string)=>{
	return post<{ reason_id: number }>('expenses/reject/reason/create', { reason });
}

var editExpenseRejectReason = async (reason_id: number, reason: string)=>{
	return post('expenses/reject/reason/edit', { reason_id, reason });
}

var deleteExpenseRejectReason = async (reason_id: number)=>{
	return post('expenses/reject/reason/delete', { reason_id });
}




var getBudgets = async (count: number, offset: number, pendings: boolean=false, department_ids: number[] = [], type_id: number = null, status_filters: number[] = [], date_start: number = null, date_end: number = null)=>{
	return post('budgets/list', { offset, count, pendings, department_ids, type_id, status_filters, date_start, date_end });
}

var searchBudgets = async (query: string, offset?: number, count?: number)=>{
	return post<Budget[]>('budgets/search', { query, offset, count });
}

var createBudget = async (data: BudgetForm)=>{
	return post<{ budget_id: number, approved: boolean }>('budgets/create', { ...data });
}

var editBudget = async (budget_id: number, data: BudgetForm)=>{
	return post('budgets/edit', { budget_id, ...data });
}

var getBudget = async (budget_id: number)=>{
	return post<Budget>('budgets/get', { budget_id });
}

var approveBudget = async (budget_id: number[])=>{
	return post('budgets/approve', { budget_id });
}

var convertToExpense = async (budget_id: number, account_id: number = null, method_id: number, file?: File, fileTypeId?: number, budget_title?: string, budget_description?: string)=>{
	var d = new FormData();
	d.append('budget_id', budget_id.toString());
	d.append('account_id', account_id?.toString() || '');
	d.append('method_id', method_id.toString());

	if(file && fileTypeId){
		d.append('asset_type_id', fileTypeId.toString());
		d.append('asset_name', budget_title || 'Presupuesto convertido a gasto');
		d.append('asset_description', budget_description || '');
		d.append('file', file);
	}

	return post<{ expense_id: number, approved: boolean }>('budgets/expense', d);
}

var deleteBudget = async (budget_id: number)=>{
	return post('budgets/delete', { budget_id });
}

var rejectBudget = async (budget_id: number, reject_reason: number)=>{
	return post('budgets/reject', { budget_id, reject_reason });
}

var paidBudget = async (budget_id: number)=>{
	return post('budgets/paid', { budget_id });
}

var getCoordinatorBusdgetsDetails = async(coordinator_id: number, date_id: number)=>{
	return post<Budget[]>('budgets/details', { coordinator_id, date_id });
}





var getUserBankAccounts = (user_id?: number)=>{
	return post<UserBankAccount[]>('users/banks/list', { user_id });
}

var createUserBankAccount = (form: BankAccountForm, user_id?: number)=>{
	return post<BankAccount>('users/banks/add', { ...form, user_id });
}

var editBankAccount = (account_id: number, name_account: string, user_id?: number)=>{
	return post('users/banks/edit', { account_id, name_account, user_id });
}

var getBankAccount = (account_id: number)=>{
	return post<BankAccount>('users/banks/get', { account_id });
}

var setPrimaryBankAccount = (account_id: number, user_id?: number)=>{
	return post('users/banks/primary', { account_id, user_id });
}

var deleteUserBankAccount = (account_id: number)=>{
	return post('users/banks/delete', { account_id });
}

var searchAccounts = async (query: string, worker: boolean = false, offset?: number, count?: number)=>{
	return post<BankAccount[]>('expenses/banks/search', { query, worker: worker || false, offset, count });
}

var addWorkerBankAccount = (worker_id: number, form: BankAccountForm)=>{
	return post('workers/banks/add', { worker_id, ...form });
}




var createWorker = async(form: WorkerForm)=>{
	return post<{ worker_id: number }>('workers/create', { ...form });
}

var editWorker = async(worker_id: number, form: WorkerForm)=>{
	return post('workers/edit', { worker_id, ...form });
}

var deleteWorker = async(worker_id: number)=>{
	return post('workers/delete', { worker_id });
}

var validateWorkerRFC = async(worker_ids: number[])=>{
	return post('workers/rfc/validate', { worker_ids });
}

var downloadWorkerCIF = async(asset_id: number)=>{
	var res = await post('workers/cif/download', { asset_id });
	if(res.error) return res;

	const response = await fetch(res.data.download_url);
	const blob = await response.blob();

	if(blob.type === 'application/json'){
		return JSON.parse(await blob.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(blob);
	link.href = url;
	link.download = `CSF-${asset_id}.pdf`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	}
}

var getWorker = async(worker_id: number)=>{
	return post<Worker>('workers/get', { worker_id });
}

var getWorkers = async(query: string, count?: number, offset?: number)=>{
	return post<Worker[]>('workers/list', { query, offset, count });
}

var getWorkerRatings = async(worker_id: number, count?: number, offset?: number)=>{
	return post('workers/ratings', { worker_id, offset, count });
}

var searchWorkers = async(query: string, offset?: number, count?: number)=>{
	return post<Worker[]>('workers/list', { query, offset, count });
}

var downloadWorkersReport = async()=>{
	var res = (await post('workers/report', {}, {
		response_type: 'blob'
	}) as any) as Blob;

	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `trabajadores_${moment().format('YYYY-MM-DD')}.csv`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	}
}

var searchCountryCity = async(query: string, offset?: number, count?: number)=>{
	return post('workers/city/search', { query, offset, count });
}

var getBanReasons = async(count?: number, offset?: number)=>{
	return post<WorkerBanReason[]>('workers/reasons/list', { offset, count });
}

var searchBanReasons = async(query: string)=>{
	return post<WorkerBanReason[]>('workers/reasons/search', { query });
}

var createWorkerBanReason = async(reason_name: string)=>{
	return post<{ reason_id: number }>('workers/reason/create', { reason_name });
}

var editWorkerBanReason = async(reason_id: number, reason_name: string)=>{
	return post('workers/reason/edit', { reason_id, reason_name });
}

var deleteWorkerBanReason = async(reason_id: number)=>{
	return post('workers/reason/delete', { reason_id });
}

var banWorker = async(worker_id: number, reason_id: number = null)=>{
	return post('workers/ban', { worker_id, reason_id });
}

var addWorkerImage = async(worker_id: number, image: File)=>{
	var d = new FormData();
	d.append('worker_id', worker_id.toString());
	d.append('image', image);
	return post<{ image_url: string }>('workers/image/add', d);
}

var deleteWorkerImage = async(worker_id: number)=>{
	return post<{ image: string | null }>('workers/image/delete', { worker_id });
}

var getTitles = async(count?: number, offset?: number)=>{
	return post<WorkerTitle[]>('workers/titles/list', { offset, count });
}

var searchTitles = async(query: string, offset?: number, count?: number)=>{
	if(!query) return cachePost('workers/titles/search', `WTTLSRCH-${offset}-${count}`, { query, offset, count });
	return post<WorkerTitle[]>('workers/titles/search', { query, offset, count });
}

var createTitle = async(title_name: string, payout_amount: number)=>{
	return post<{ title_id: number }>('workers/titles/create', { title_name, payout_amount });
}

var editTitle = async(title_id: number, title_name: string, payout_amount: number)=>{
	return post('workers/titles/edit', { title_id, title_name, payout_amount });
}

var deleteTitle = async(title_id: number)=>{
	return post('workers/titles/delete', { title_id });
}

var addEventWorker = async(date_id: number, worker_id: number, title_id: number, description: string, payout_amount: number)=>{
	return post('events/workers/add', { date_id, worker_id, title_id, description, payout_amount });
}

var editEventWorker = async(date_id: number, worker_id: number, title_id: number, description: string, payout_amount: number)=>{
	return post('events/workers/edit', { date_id, worker_id, title_id, description, payout_amount });
}

var cancelEventWorker = async(date_id: number, worker_id: number)=>{
	return post('events/workers/cancel', { date_id, worker_id });
}

var rateEventWorker = async(date_id: number, worker_id: number, rating: number, rating_comment: string)=>{
	return post('events/workers/rate', { date_id, worker_id, rating, rating_comment });
}

var sendEventWorkersSMS = async(date_id: number, worker_ids: number[])=>{
	return post('events/workers/sms', { date_id, worker_ids });
}

var getWorkerConfirmation = async(token: string)=>{
	return post<ConfirmationData>('events/confirm/info', { token }, { login: false });
}

var workerInvitationResponse = async(token: string, declined: boolean)=>{
	return post<boolean>('events/confirm/response', { token, declined }, { login: false });
}

var getContracts = async()=>{
	return post<Contract []>('contracts/list');
}

var getContract = async(contract_id: number)=>{
	return post<Contract>('contracts/get', { contract_id });
}

var updateContract = async(contract_id: number, contract_title: string, content: string)=>{
	return post('contracts/update', { contract_id, contract_title, content });
}

var getAvailableWorkers = async(date_id: number, query: string = null, count: number = 20, offset: number = 0, title_id: number = null)=>{
	return post('events/workers/available', { date_id, query, count, offset, title_id });
}

var eventPayoutPreparation = async(date_id: number)=>{
	return post('events/payouts/prepare', { date_id });
}

var eventPayoutCreation = async(date_id: number, workers: number[])=>{
	return post('events/payouts/create', { date_id, workers });
}

var getPayoutsList = async(count: number, offset: number, pendings: boolean=false, payouts?: number[], date_start: number=null, date_end: number=null)=>{
	return post<EventPayout[]>('payouts/list', { offset, count, pendings, payouts, date_start, date_end });
}

var getUnliquidatedEvents = async()=>{
	return post<EventDateList[]>('payouts/unliquidated', {});
}

var searchPayouts = async(query: string, count?: number, offset?: number)=>{
	return post<EventPayout[]>('payouts/search', { query, offset, count });
}

var getPayout = async(payout_id: number)=>{
	return post<EventPayout>('payouts/get', { payout_id });
}

var paidPayment = async(payout_id: number, ids: number[], paid: boolean = true)=>{
	return post('payouts/paid', { payout_id, ids, paid });
}

var cancelPayment = async(payout_id: number, ids: number[])=>{
	return post('payouts/cancel', { payout_id, ids });
}

var editPayment = async(payout_id: number, worker_id: number, amount: number, comment: string = null)=>{
	return post('payouts/edit', { payout_id, worker_id, amount, comment });
}

var getPayoutWorkers = async(payout_id: number, condition: boolean)=>{
	return post('payouts/workers/get', { payout_id, condition });
}

var downloadPayoutsZip = async(payouts: number[])=>{
	var res = (await post('payouts/zip', { payouts }, {
		response_type: 'blob'
	}) as any) as Blob;

	var blob = new Blob([res], { type: 'application/zip' });
	var url = window.URL.createObjectURL(blob);
	var a = document.createElement('a');
	a.href = url;
	a.download = `STPLiq-${moment().format('YYYY-MM-DD')}.zip`;
	document.body.appendChild(a);
	a.click();
	window.URL.revokeObjectURL(url);
}

var completePayout = async(payouts: number [], completed: boolean = true)=>{
	return post('payouts/complete', { payouts, completed });
}




var getFormsList = (count: number, offset: number)=>{
	return post<Metadata[]>('forms/list', { offset, count });
}

var getForm = (metadata_id: number)=>{
	return post('forms/get', { metadata_id});
}

var createForm = async(type_id: number, metadata_name: string, metadata_comment: string, preadded: boolean, value: string)=>{
	return post('forms/create', {type_id, metadata_name, metadata_comment, preadded, value });
}

var deleteForm = async(metadata_id: number, deleted: boolean)=>{
	return post('forms/delete', { metadata_id, deleted });
}

var editForm = async(metadata_id: number, type_id: number, metadata_name: string, metadata_comment: string, preadded: boolean, default_value: string)=>{
	return post('forms/edit', { metadata_id, type_id, metadata_name, metadata_comment, preadded, default_value });
}

var createFormValue = async(metadata_id: number, value: string)=>{
	return post('forms/value/create', { metadata_id, value });
}

var editFormValue = async(value_id: number, value: string)=>{
	return post('forms/value/edit', { value_id, value });
}

var deleteFormValue = async(value_id: number, deleted: boolean = true)=>{
	return post('forms/value/delete', { value_id, deleted });
}

var searchForms = async(query: string, offset?: number, count?: number, options: boolean = false)=>{
	return post('forms/search', { query, offset, count, options });
}

var getMetadataValues = (metadata_id: number)=>{
	return cachePost<{
		value_id: number,
		value: string
	}[]>('forms/value/list', `MVLS-${metadata_id}`, { metadata_id });
}

var saveFormsSort = (forms: number[])=>{
	return post('forms/sort/update', { forms });
}




var getEventForm = (date_id: number)=>{
	return post<EventDateMetadata[]>('events/form/get', { date_id });
}

var refreshPreaddedMetadata = (date_id: number)=>{
	return post<EventDateMetadata[]>('events/form/preadded', { date_id });
}

var updateFormAnswer = (date_id: number, metadata_id: number, value_id: number, value: string)=>{
	return post('events/form/answer', {
		date_id, metadata_id, value_id, value
	});
}

var getAvailableMetadata = (date_id: number)=>{
	return post<Metadata[]>('events/form/metadata', { date_id });
}

var approveExpense = async (expenses: number [])=>{
	return post('expenses/approve', { expenses });
}





var getPromoters = async(count?: number, offset?: number)=>{
	return post<Promoter []>('promoters/list', { offset, count });
}

var getPromoter = async(promoter_id: number)=>{
	return post<Promoter>('promoters/get', { promoter_id });
}

var getPromoterContacts = async(promoter_id: number, offset?: number, count?: number)=>{
	return post<PromoterContact[]>('promoters/contacts/get', { promoter_id, offset, count });
}

var updatePromoterStatus = async(promoter_id: number, status_id: number)=>{
	return post('promoters/status/update', { promoter_id, status_id });
}

var searchPromoters = async(query: string, offset?: number, count?: number)=>{
	return post<Promoter []>('promoters/search', { query, offset, count });
}

var getPromoterEvents = async(promoter_id: number, count?: number, offset?: number)=>{
	return post<EventDate []>('promoters/events', { promoter_id, count, offset });
}

var createPromoterContact = async(promoter_id: number, contact_name: string, email: string, phone: string, department: string)=>{
	return post<{ contact_id: number }>('promoters/contacts/create', { promoter_id, contact_name, email, phone, department });
}

var updatePromoterContact = async(contact_id: number, contact_name: string, email: string, phone: string, department: string)=>{
	return post('promoters/contacts/edit', { contact_id, contact_name, email, phone, department });
}

var getPromoterContact = async(contact_id: number)=>{
	return post<PromoterContact>('promoters/contact/get', { contact_id });
}




var getEmploymentsList = async(count: number, offset: number)=>{
	return post('employments/list', { count, offset });
}

var searchEmployments = async(query: string, offset?: number, count?: number)=>{
	return post('employments/search', { query, offset, count });
}

var getEmployment = async(employment_id: number)=>{
	return post('employments/get', { employment_id });
}

var createEmployment = async(employment_name: string, date_start: number, date_end: number, location: Location, allow_remote: boolean, geofence_radius: number, late_seconds: number, penalty_seconds: number)=>{
	return post('employments/create', { employment_name, date_start, date_end, location, allow_remote, geofence_radius, late_seconds, penalty_seconds });
}

var editEmployment = async(employment_id: number, employment_name?: string, date_start?: number, date_end?: number, location?: Location, allow_remote?: boolean, geofence_radius?: number, late_seconds?: number, penalty_seconds?: number)=>{
	return post('employments/edit', { employment_id, employment_name, date_start, date_end, location, allow_remote, geofence_radius, late_seconds, penalty_seconds });
}

var deleteEmployment = async(employment_id: number, deleted: boolean = true)=>{
	return post('employments/delete', { employment_id, deleted });
}

var getEmploymentSchedules = async(employment_id: number)=>{
	return post('employments/schedules/list', { employment_id });
}

var getEmploymentSchedule = async(schedule_id: number)=>{
	return post('employments/schedules/get', { schedule_id });
}

var createEmploymentSchedule = async(employment_id: number, day: number, time_start: number, time_end: number, salary: number)=>{
	return post('employments/schedules/create', { employment_id, day, time_start, time_end, salary });
}

var editEmploymentSchedule = async(schedule_id: number, day?: number, time_start?: number, time_end?: number, salary?: number)=>{
	return post('employments/schedules/edit', { schedule_id, day, time_start, time_end, salary });
}

var deleteEmploymentSchedule = async(schedule_id: number)=>{
	return post('employments/schedules/delete', { schedule_id });
}

var addEmploymentWorker = async(schedule_id: number, user_id: number)=>{
	return post('employments/workers/add', { schedule_id, user_id });
}

var removeEmploymentWorker = async(schedule_id: number, user_id: number)=>{
	return post('employments/workers/remove', { schedule_id, user_id });
}

var getEmploymentUsers = async(employment_id: number)=>{
	return post('employments/users', { employment_id });
}

var getCheckinUsers = async(count?: number, offset?: number, query?: string)=>{
	return post('employments/employees/users/list', { count, offset, query });
}

var assignSchedulesToUser = async(user_id: number, schedule_ids: number[], schedule_type_ids: number[])=>{
	return post('employments/schedules/assign', { user_id, schedule_ids, schedule_type_ids });
}

var createEmploymentIncidences = async(employment_id: number, user_id: number, schedule_ids: number[], log_type_id: number, date_start: number, date_end: number, notes?: string)=>{
	return post('employments/employees/incidences/create', { employment_id, user_id, schedule_ids, log_type_id, date_start, date_end, notes });
}

var deleteEmploymentIncidence = async(log_id: number)=>{
	return post('employments/employees/incidences/delete', { log_id });
}

var getActiveEmploymentWorkers = async(date_start: number, date_end: number, count: number, offset: number)=>{
	return post('employments/employees/active', { date_start, date_end, count, offset });
}

var employmentCheckIn = async(user_id: number, schedule_id: number, date: number)=>{
	return post('employments/employees/checkin', { user_id, schedule_id, date });
}

var employmentCheckOut = async(log_id: number, date: number)=>{
	return post('employments/employees/checkout', { log_id, date });
}

var employmentEditCheckIn = async(log_id: number, date_start: number, date_end: number)=>{
	return post('employments/employees/checkin/edit', { log_id, date_start, date_end });
}

var getUserAssistance = async(user_id: number, date_start: number, date_end: number)=>{
	return post('employments/employees/assistance', { user_id, date_start, date_end });
}

var getWeeklyPayments = async(date_end: number)=>{
	return post('employments/employees/payments/list', { date_end });
}

var getUserWeeklyLogs = async(user_id: number, date_end: number)=>{
	return post('employments/employees/payments/user', { user_id, date_end });
}

var downloadPaymentsCsv = async(endpoint: string, data: any, name: string)=>{
	var res = (await post(endpoint, data, {
		response_type: 'blob'
	}) as any) as Blob;

	if(!(res instanceof Blob)) return res as any;
	if(res.type === 'application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	link.href = URL.createObjectURL(res);
	link.download = `${name}-Pagos.csv`;
	link.click();
	link.remove();

	return { error: false, code: null, message: null, data: null };
}

var downloadPaymentsReport = async(name: string, user_id?: number, payout_id?: number)=>{
	return downloadPaymentsCsv('employments/employees/payments/report', { user_id, payout_id }, name);
}

var downloadWorkerPaymentsReport = async(name: string, worker_id: number)=>{
	return downloadPaymentsCsv('workers/payments/report', { worker_id }, name);
}

var getEmploymentAdjustmentOptions = async(user_id: number)=>{
	return post('employments/employees/payments/adjustment/options', { user_id });
}

var createEmploymentAdjustment = async(user_id: number, date: number, amount: number, notes?: string, employment_id?: number)=>{
	return post('employments/employees/payments/adjustment/create', { user_id, date, amount, notes, employment_id });
}

var confirmWeeklyPayments = async(log_ids: number[])=>{
	return post('employments/employees/payments/confirm', { log_ids });
}

var deleteWeeklyPaymentLog = async(log_id: number)=>{
	return post('employments/employees/payments/log/delete', { log_id });
}

var generateEmploymentPayout = async()=>{
	return post('employments/payments/generate', {});
}

var listEmploymentPayouts = async(count?: number, offset?: number)=>{
	return post('employments/payments/payouts/list', { count, offset });
}

var getEmploymentPayout = async(payout_id: number)=>{
	return post('employments/payments/payouts/get', { payout_id });
}

var getEmploymentPayoutUserLogs = async(payout_id: number, user_id: number)=>{
	return post('employments/payments/payouts/logs', { payout_id, user_id });
}

var getEmploymentAdjustmentTypes = async()=>{
	return post('employments/payments/adjustment/types/list', {});
}

var createEmploymentAdjustmentType = async(type_name: string)=>{
	return post('employments/payments/adjustment/types/create', { type_name });
}

var editEmploymentAdjustmentType = async(type_id: number, type_name: string)=>{
	return post('employments/payments/adjustment/types/edit', { type_id, type_name });
}

var createEmploymentPayoutAdjustment = async(payout_id: number, user_id: number, type_id: number, amount: number)=>{
	return post('employments/payments/payouts/adjustment/create', { payout_id, user_id, type_id, amount });
}

var deleteEmploymentPayoutAdjustment = async(payout_user_id: number)=>{
	return post('employments/payments/payouts/adjustment/delete', { payout_user_id });
}

var payEmploymentPayout = async(payout_id: number, user_ids: number[], paid: boolean = true)=>{
	return post('employments/payments/payouts/pay', { payout_id, user_ids, paid });
}

var cancelEmploymentPayout = async(payout_id: number, user_ids: number[])=>{
	return post('employments/payments/payouts/cancel', { payout_id, user_ids });
}

var deleteEmploymentPayout = async(payout_id: number)=>{
	return post('employments/payments/payouts/delete', { payout_id });
}

var editEmploymentPayout = async(payout_id: number, user_id: number, amount: number)=>{
	return post('employments/payments/payouts/edit', { payout_id, user_id, amount });
}

var downloadEmploymentPayoutStp = async(payout_id: number, user_ids?: number[])=>{
	var res = (await post('employments/payments/payouts/stp', { payout_id, user_ids }, {
		response_type: 'blob'
	}) as any) as Blob;

	var blob = new Blob([res], { type: 'application/zip' });
	var url = window.URL.createObjectURL(blob);
	var a = document.createElement('a');
	a.href = url;
	a.download = `STPNomina-${moment().format('YYYY-MM-DD')}.zip`;
	document.body.appendChild(a);
	a.click();
	a.remove();
	window.URL.revokeObjectURL(url);
}

var downloadEmploymentPayoutBanorte = async(payout_id: number, user_ids?: number[])=>{
	var res = (await post('employments/payments/payouts/banorte', { payout_id, user_ids }, {
		response_type: 'blob'
	}) as any) as Blob;

	var blob = new Blob([res], { type: 'text/csv' });
	var url = window.URL.createObjectURL(blob);
	var a = document.createElement('a');
	a.href = url;
	a.download = `Banorte-${moment().format('YYYY-MM-DD')}.csv`;
	document.body.appendChild(a);
	a.click();
	a.remove();
	window.URL.revokeObjectURL(url);
}

var downloadEmploymentAssistanceReport = async(date_start: number, date_end: number, user_id?: number, by_day: boolean=false)=>{
	var res = (await post('employments/employees/report', { date_start, date_end, user_id, by_day }, {
		response_type: 'blob'
	}) as any) as Blob;

	if(res.type === 'application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `asistencias${user_id ? `_usuario-${user_id}` : ''}_${moment.unix(date_start).format('DD-MM-YY')}-${moment.unix(date_end).format('DD-MM-YY')}.csv`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	}
}

var removeEmployee = async(user_id: number, employment_id: number)=>{
	return post('employments/employee/remove', { user_id, employment_id });
}

interface AssetOptions{
	user_asset_type?: UserAssetType,
	inventory_item_asset_type?: InventoryItemAssetType,
	worker_id?: number,
	user_id?: number,
	date_id?: number,
	expense_id?: number,
	inventory_item_id?: number,
	reference?: string,
}
var createAsset = (type_id: AssetType, asset_name: string, description: string, file: File, options?: AssetOptions)=>{
	var d = new FormData();

	d.append('asset_name', asset_name);
	d.append('type_id', type_id.toString());
	d.append('description', description);
	if(options?.worker_id){
		d.append('worker_id', options.worker_id.toString());
		d.append('user_asset_type', options.user_asset_type.toString());
	}else if(options?.user_id){
		d.append('user_id', options.user_id.toString());
		d.append('user_asset_type', options.user_asset_type.toString());
	}else if(options?.date_id){
		d.append('date_id', options.date_id.toString());
	}else if(options?.expense_id){
		d.append('expense_id', options.expense_id.toString());
		if(options?.reference) d.append('reference', options.reference);
	}else if (options?.inventory_item_id) {
      d.append('inventory_item_id', options.inventory_item_id.toString());
		d.append('inventory_item_asset_type', options.inventory_item_asset_type.toString());
   }
	d.append('file', file);


	return post('site/asset/create', d);
}

var deleteAsset = async(asset_id: number)=>{
	return post('site/asset/delete', { asset_id });
}




var getShippingCarriers = async ()=>{
   return post<ShippingCarrier[]>('shipment/carriers/list');
}

var getInventoryType = async (type_id: number)=>{
	return post<InventoryType>('inventory/type/get', { type_id });
}

var getInventoryTypes = async (query?: string, offset?: number, count?: number)=>{
   return post<InventoryType[]>('inventory/type/list', { query, offset, count });
}

var createInventoryType = async (type_name: string, type_icon: string, consumable?: boolean)=>{
   return post<{ type_id: number }>('inventory/type/create', { type_name, type_icon, consumable: consumable ? 1 : 0 });
}

var editInventoryType = async (type_id: number, type_name?: string, type_icon?: string, consumable?: boolean)=>{
   return post('inventory/type/edit', { type_id, type_name, type_icon, consumable: consumable ? 1 : 0 });
}

var deleteInventoryType = async (type_id: number)=>{
   return post('inventory/type/delete', { type_id });
}

var getWarehouse = async (warehouse_id: number)=>{
	return post('warehouse/get', { warehouse_id });
}

var getWarehouseItems = async (warehouse_id: number, offset: number = 0, count: number = 20)=>{
	return post<{ items: GroupedItemsByType[], total: number }>('warehouse/items', { warehouse_id, offset, count });
}

var getWarehouses = async(offset: number = 0, count: number = 100)=>{
   return post<InventoryWarehouse[]>('warehouse/list', { offset, count });
}

var searchWarehouses = async (query: string, offset?: number, count?: number)=>{
	return post<InventoryWarehouse[]>('warehouse/search', { query, offset, count });
}

var createInventoryWarehouse = async (warehouse_name: string, capacity: number, description?: string, location?: Location)=>{
   return post<{ warehouse_id: number }>('warehouse/create', { warehouse_name, description, location, capacity });
}

var editInventoryWarehouse = async (warehouse_id: number, capacity: number, warehouse_name?: string, description?: string)=>{
   return post('warehouse/edit', { warehouse_id, warehouse_name, description, capacity });
}

var deleteInventoryWarehouse = async (warehouse_id: number)=>{
   return post('warehouse/delete', { warehouse_id });
}

var getInventoryItemsByType = async (warehouse_id: number, type_id: number, offset?: number, count?: number, query?: string)=>{
   return post<InventoryItem[]>('inventory/item/list/type', { warehouse_id, type_id, offset, count, query });
}

var getInventoryItem = async (item_id: number, assets: boolean = false, logs: boolean = false)=>{
   return post<InventoryItem>('inventory/item/get', { item_id, assets, logs });
}

var createInventoryItem = async (form: Partial<InventoryItem>)=>{
   return post<{ item_id: number }>('inventory/item/create', { ...form });
}

var editInventoryItem = async (item_id: number, form: Partial<InventoryItem>)=>{
   return post('inventory/item/edit', { item_id, ...form });
}

var deleteInventoryItem = async (item_id: number, deleted?: boolean)=>{
   return post('inventory/item/delete', { item_id, deleted: deleted });
}

var transferInventoryItems = async(item_ids: number[], warehouse_id: number)=>{
	return post('inventory/item/transfer', { item_ids, warehouse_id });
}

var searchItems = async(query: string, offset?: number, count?: number, type_id?: number, warehouse_id?: number)=>{
	return post<InventoryItem[]>('inventory/item/search', { query, offset, count, type_id, warehouse_id });
}

var getInventoryOutOfWarehouse = async(offset?: number, count?: number, query?: string, status_filters?: number[])=>{
	return post<InventoryItem[]>('inventory/items', { offset, count, query, status_filters });
}

var getShipment = async(shipment_id: number)=>{
	return post<Shipment>('shipment/get', { shipment_id });
}

var getShipments = async(offset: number = 0, count: number = 30, query: string = null, status_filters: number[] = [])=>{
	return post<Shipment[]>('shipment/list', { offset, count, query, status_filters });
}

var getAvailableItemsForShipment = async(destination_zipcode: string | null, query: string = null, type_id: number = null, offset: number = 0, count: number = 20)=>{
	return post<AvailableItemForShipment[]>('shipment/available-items', { destination_zipcode, query, type_id, offset, count });
}

var createShipment = async(data: { request_id?: number, request_reference_only?: boolean, parent_shipment_id?: number, warehouse_id?: number, destination?: Location, item_ids: number[], description?: string, date_return?: number })=>{
	return post<{ shipment_id: number }>('shipment/create', data);
}

var deleteShipment = async(shipment_id: number)=>{
	return post('shipment/delete', { shipment_id });
}

var editShipmentUser = async(shipment_id: number, user_assigned: number)=>{
	return post('shipment/edit/user', { shipment_id, user_assigned });
}

var editShipmentReturnDate = async(shipment_id: number, date_return: number)=>{
	return post('shipment/edit/return-date', { shipment_id, date_return });
}

var shipShipmentItems = async(shipment_id: number, item_ids: number[], data: { carrier_id?: number, tracking_number?: string })=>{
	return post('shipment/ship', { shipment_id, item_ids, ...data });
}

var deliverShipmentItems = async(shipment_id: number, item_ids: number[], data: { user_recipient?: number, recipient_name?: string, recipient_contact?: string })=>{
	return post('shipment/deliver', { shipment_id, item_ids, ...data });
}

var addShipmentItems = async(shipment_id: number, item_ids: number[])=>{
	return post('shipment/item/add', { shipment_id, item_ids });
}

var removeShipmentItem = async(shipment_id: number, item_id: number)=>{
	return post('shipment/item/remove', { shipment_id, item_id });
}

var getRequests = async(offset: number = 0, count: number = 50, query: string = null, status_filters: number[] = [], with_items_only: boolean = false, express_only: boolean = false)=>{
	return post('request/list', { offset, count, query, status_filters, with_items_only, express_only });
}

var createRequest = async(data: {user_assigned?: number, date_return: number, box_office_start_date?: number, description?: string, items: { type_id: number, quantity: number }[], events: number[],})=>{
	return post<{ request_id: number, types?: number[] }>('request/create', data);
}

var createExpressRequest = async(data: {user_assigned?: number, date_return: number, description: string, events?: number[]})=>{
	return post<{ request_id: number }>('request/create-express', data);
}

var getRequest = async(request_id: number)=>{
	return post<Request>('request/get', { request_id });
}

var updateRequestItem = async(request_id: number, type_id: number, quantity: number, remove: boolean)=>{
	return post('request/update/item', { request_id, type_id, quantity, remove });
}

var updateRequestEvent = async(request_id: number, date_id: number, remove: boolean)=>{
	return post('request/update/event', { request_id, date_id, remove });
}

var editRequestInfo = async(request_id: number, date_return: number, box_office_start_date?: number, description?: string)=>{
	return post('request/edit/info', { request_id, date_return, box_office_start_date, description });
}

var approveRequest = async(request_id: number)=>{
	return post('request/approve', { request_id });
}

var rejectRequest = async(request_id: number, rejection_reason: string)=>{
	return post('request/reject', { request_id, rejection_reason });
}

var finalizeRequest = async(request_id: number)=>{
	return post('request/finalize', { request_id });
}

var downloadTicketsReport = async(date_start: number, date_end: number)=>{
	var res = (await post('reports/tickets', { date_start, date_end }, {
		response_type: 'blob'
	}) as any) as Blob;

	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `AFOROS-${moment.unix(date_start).format('DD/MM/YY')}-${moment.unix(date_end).format('DD/MM/YY')}.csv`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	}
}

var getEmployeesRFCList = async()=>{
	return post<RFCWorker[]>('reports/employees/rfc/list', {});
}

var downloadEmployeesRFCReport = async(worker_ids?: number[])=>{
	var res = (await post('reports/employees/rfc', { worker_ids }, {
		response_type: 'blob'
	}) as any) as Blob;

	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}
	if(res.size===0){
		return { error: true, code: 'RPT-EMP-1', message: 'No hay registros para generar el reporte.', data: null }
	}
	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `validacion_rfc_${moment().format('YYYY-MM-DD')}.csv`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	}
}

var downloadExpensesReport = async(date_start: number, date_end: number, departments: number[], statuses: number[], types: number[])=>{
	var res = (await post('reports/expenses', { date_start, date_end, departments, statuses, types }, {
		response_type: 'blob'
	}) as any) as Blob;

	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `GASTOS-${moment.unix(date_start).format('DD/MM/YY')}-${moment.unix(date_end).format('DD/MM/YY')}.csv`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	}
}

var downloadEventsReport = async(date_start: number, date_end: number)=>{
	var res = (await post('reports/events', { date_start, date_end }, {
		response_type: 'blob'
	}) as any) as Blob;

	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `EVENTOS-${moment.unix(date_start).format('DD/MM/YY')}-${moment.unix(date_end).format('DD/MM/YY')}.csv`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	}
}

var getExpensesReportSummary = async(date_start: number, date_end: number, departments: number[], statuses: number[], types: number[])=>{
	return post<ExpensesReportSummary>('reports/expenses/summary', { date_start, date_end, departments, statuses, types });
}

var getEventsReportSummary = async(date_start: number, date_end: number)=>{
	return post<EventsReportSummary>('reports/events/summary', { date_start, date_end });
}

var downloadCoordinatorBudgetsReport = async(user_id: number, date_start: number, date_end: number)=>{
	var res = (await post('reports/budgets/coordinator', { user_id, date_start, date_end }, {
		response_type: 'blob'
	}) as any) as Blob;

	if(res.type === 'application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `PRESUPUESTOS-COORDINADOR-${user_id}-${moment.unix(date_start).format('DD-MM-YY')}-${moment.unix(date_end).format('DD-MM-YY')}.csv`;
	link.click();
	link.remove();

	return { error: false, code: null, message: null, data: null };
}

var downloadWorkersStatusReport = async()=>{
	var res = (await post('reports/workers', {}, {
		response_type: 'blob'
	}) as any) as Blob;

	if(res.type === 'application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `COLABORADORES.csv`;
	link.click();
	link.remove();

	return { error: false, code: null, message: null, data: null };
}

var getAllIssueType=(query?: string, offset?: number, count?: number)=>{
	return post<IssueType[]>('issue/types/list', { query, offset, count });
}

var createIssueType=(data: { type_name: string; type_icon?: string })=>{
	return post<{ type_id: number }>('issue/types/create', data);
}

var editIssueType=(data: { type_id: number; type_name: string; type_icon?: string })=>{
	return post<boolean>('issue/types/update', data);
}

var deleteIssueType=(type_id: number)=>{
	return post<boolean>('issue/types/delete', { type_id });
}

var createIssue=(issue: Issue)=>{
	return post<number>('issue/create', issue);
}

var getAllIssues=(filters?: { priority?: number, tags_ids?: number[], search?: string, date_start?: number, date_end?: number, status_ids?: number[], limit?: number, offset?: number })=>{
	return post<{ items: Issue[], total: number }>('issue/list', filters || {});
}

var getIssue=(id: number)=>{
	return post<Issue>('issue/get', { id });
}

var updateIssue=(issue: Issue)=>{
	return post<boolean>('issue/update', issue);
}

var updateIssueStatus=(issue_id: number, status_id: number)=>{
	return post<boolean>('issue/update/status', { issue_id, status_id });
}

var getIssueComments = async(issue_id: number, count?: number, offset?: number)=>{
	return post<IssueComment[]>('issue/comments/list', { issue_id, count, offset });
}

var createIssueComment = async(issue_id: number, content: string, mentioned_users: number[] = [])=>{
	return post<{ comment_id: number }>('issue/comments/create', { issue_id, content, mentioned_users });
}

var updateIssueComment = async(comment_id: number, content: string)=>{
	return post<boolean>('issue/comments/update', { comment_id, content });
}

var deleteIssue = async(issue_id: number)=>{
	return post<boolean>('issue/delete', { issue_id });
}

var deleteIssueComment = async(comment_id: number)=>{
	return post<boolean>('issue/comments/delete', { comment_id });
}

var removeIssueEvent=(issue_id: number, date_id: number)=>{
	return post<boolean>('issue/event/delete', { issue_id, date_id });
}

var removeIssueUser = async(issue_id: number, user_id: number)=>{
	return post<boolean>('issue/user/delete', { issue_id, user_id });
}

var removeIssueType = async(issue_id: number, type_id: number)=>{
	return post<boolean>('issue/type/delete', { issue_id, type_id });
}

export default {

	createAsset,
	deleteAsset,

	getIssue,
	createIssue,
	updateIssue,
	updateIssueStatus,
	deleteIssue,
	getAllIssues,
	getIssueComments,
	createIssueComment,
	updateIssueComment,
	deleteIssueComment,
	removeIssueEvent,
	removeIssueUser,
	removeIssueType,
	
	dismissNotification,
	dismissAllNotifications,
	editUserTags,
	editUser,
	getPayoutTypes,
	setUserPayoutType,
	getAllTags,
	getAllIssueType,
	createIssueType,
	editIssueType,
	deleteIssueType,
	getAuthUrl,
	getLogin,
	getNotifications,
	getNotificationsInbox,
	getToken,
	getUser,
	getUserBudgets,
	getUserExpenses,
	getUserRequests,
	getUsers,
	logout,
	onLoginToken,
	searchUsers,
	userAccess,


	addEventLocation,
	assignEventCoordinator,
	assignEventExecutive,
	assignEventPromoterContact,
	assignParentEvent,
	updateEventDate,
	assingEventUser,
	closeEvent,
	createEventComment,
	deleteEventComment,
	editEventComment,
	getEvent,
	getEventAssets,
	getEventBudgets,
	getEventComments,
	getEventExpenses,
	getEventPayouts,
	getEvents,
	getMyEvents,
	getEventsCount,
	addEventWorkerRequest,
	deleteEventWorkerRequest,
	getEventWorkers,
	getMonthEvents,
	getMonthStatusStats,
	searchEvents,
	updateEventStatus,
	unassignEvent,


	approveExpense,
	createExpense,
	createExpenseRejectReason,
	createExpenseType,
	createPaymentMethod,
	deleteExpense,
	deleteExpenseRejectReason,
	deleteExpenseType,
	deletePaymentMethod,
	editExpense,
	editExpenseRejectReason,
	editExpenseType,
	editPaymentMethod,
	getExpense,
	getExpenses,
	getExpenseRejectReasons,
	getExpenseTypes,
	getPaymentMethods,
	paidExpense,
	rejectExpense,
	searchExpenses,


	createBudget,
	editBudget,
	getBudget,
	getBudgets,
	getCoordinatorBusdgetsDetails,
	searchBudgets,
	approveBudget,
	convertToExpense,
	deleteBudget,
	rejectBudget,
	paidBudget,


	getUserBankAccounts,
	createUserBankAccount,
	editBankAccount,
	getBankAccount,
	setPrimaryBankAccount,
	deleteUserBankAccount,
	searchAccounts,
	addWorkerBankAccount,


	createWorker,
	editWorker,
	deleteWorker,
	validateWorkerRFC,
	downloadWorkerCIF,
	getWorker,
	getWorkers,
	getWorkerRatings,
	searchWorkers,
	downloadWorkersReport,
	searchCountryCity,
	addWorkerImage,
	deleteWorkerImage,


	getBanReasons,
	searchBanReasons,
	createWorkerBanReason,
	editWorkerBanReason,
	deleteWorkerBanReason,
	banWorker,


	getTitles,
	searchTitles,
	createTitle,
	editTitle,
	deleteTitle,


	addEventWorker,
	editEventWorker,
	cancelEventWorker,
	rateEventWorker,
	sendEventWorkersSMS,
	getWorkerConfirmation,
	workerInvitationResponse,
	getAvailableWorkers,
	getContracts,
	getContract,
	updateContract,


	eventPayoutPreparation,
	eventPayoutCreation,
	getPayoutsList,
	getUnliquidatedEvents,
	searchPayouts,
	getPayout,
	paidPayment,
	cancelPayment,
	editPayment,
	getPayoutWorkers,
	downloadPayoutsZip,
	completePayout,


	getFormsList,
	getForm,
	createForm,
	deleteForm,
	editForm,
	createFormValue,
	editFormValue,
	deleteFormValue,
	saveFormsSort,
	searchForms,
	getMetadataValues,


	getEventForm,
	refreshPreaddedMetadata,
	updateFormAnswer,
	getAvailableMetadata,


	createPromoterContact,
	updatePromoterContact,
	getPromoters,
	getPromoter,
	getPromoterContacts,
	searchPromoters,
	updatePromoterStatus,
	getPromoterEvents,
	getPromoterContact,


	getShippingCarriers,
	getInventoryType,
   getInventoryTypes,
   createInventoryType,
   editInventoryType,
   deleteInventoryType,
   getWarehouse,
   getWarehouseItems,
   getWarehouses,
   searchWarehouses,
   createInventoryWarehouse,
   editInventoryWarehouse,
   deleteInventoryWarehouse,
   getInventoryItemsByType,
   getInventoryItem,
   createInventoryItem,
	editInventoryItem,
   deleteInventoryItem,
	transferInventoryItems,
	searchItems,
	getInventoryOutOfWarehouse,

	getShipment,
	getShipments,
	getAvailableItemsForShipment,
	createShipment,
	deleteShipment,
	editShipmentUser,
	editShipmentReturnDate,
	shipShipmentItems,
	deliverShipmentItems,
	addShipmentItems,
	removeShipmentItem,


	createRequest,
	createExpressRequest,
	getRequest,
	getRequests,
	updateRequestItem,
	updateRequestEvent,
	editRequestInfo,
	approveRequest,
	rejectRequest,
	finalizeRequest,


	getEmploymentsList,
	searchEmployments,
	getEmployment,
	createEmployment,
	editEmployment,
	deleteEmployment,
	getEmploymentSchedules,
	getEmploymentSchedule,
	createEmploymentSchedule,
	editEmploymentSchedule,
	deleteEmploymentSchedule,
	addEmploymentWorker,
	removeEmploymentWorker,
	getEmploymentUsers,
	getCheckinUsers,
	getWeeklyPayments,
	getUserWeeklyLogs,
	getEmploymentAdjustmentOptions,
	createEmploymentAdjustment,
	confirmWeeklyPayments,
	deleteWeeklyPaymentLog,
	generateEmploymentPayout,
	listEmploymentPayouts,
	getEmploymentPayout,
	getEmploymentPayoutUserLogs,
	createEmploymentPayoutAdjustment,
	deleteEmploymentPayoutAdjustment,
	getEmploymentAdjustmentTypes,
	createEmploymentAdjustmentType,
	editEmploymentAdjustmentType,
	payEmploymentPayout,
	cancelEmploymentPayout,
	editEmploymentPayout,
	deleteEmploymentPayout,
	downloadEmploymentPayoutStp,
	downloadEmploymentPayoutBanorte,
	assignSchedulesToUser,
	createEmploymentIncidences,
	deleteEmploymentIncidence,
	getActiveEmploymentWorkers,
	employmentCheckIn,
	employmentCheckOut,
	employmentEditCheckIn,
	getUserAssistance,
	downloadEmploymentAssistanceReport,
	downloadPaymentsReport,
	downloadWorkerPaymentsReport,
	removeEmployee,


	downloadTicketsReport,
	downloadExpensesReport,
	downloadEventsReport,
	getExpensesReportSummary,
	getEventsReportSummary,
	downloadEmployeesRFCReport,
	getEmployeesRFCList,
	downloadCoordinatorBudgetsReport,
	downloadWorkersStatusReport,
}
